import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { getCart } from 'api/operations/cartServiceV2';
import { pickCarriers } from 'api/operations/enrollmentService';
import { getAvailableCarriers } from 'api/operations/productServiceV2';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';
import { Carrier, RFQCarrierAvailability } from 'types/productServiceV2';
import { InputRFQCarrierByPLine } from 'types/enrollmentService';
import ProductLineDelegate from './productLineDelegate';

export enum ProductLineCardStatus {
  NO_CARRIERS_REQUESTED = 'NO_CARRIERS_REQUESTED',
  MISSING_RECIPIENTS = 'MISSING_RECIPIENTS',
  INCLUDED_IN_REQUEST = 'INCLUDED_IN_REQUEST',
}

export interface CarriersPerProductLine {
  productLineId: ProductLineId;
  carriers: CarrierUiPresenter[];
  status: ProductLineCardStatus;
}

export interface CarrierUiPresenter extends Carrier {
  /**
   * Carrier checkbox status
   */
  checked: boolean;
  /**
   * Additional CC recipient emails
   */
  ccRecipients: string[];
  /**
   * Represents the current carrier status, used to determine the final product line status.
   */
  status: ProductLineCardStatus;
}

export default class PickCarriersPageStore {
  private _isLoading = false;
  private _carriersPerProductLine: ProductLineDelegate[] = [];
  private _groupId = '';

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get carriersPerProductLine(): ProductLineDelegate[] {
    return this._carriersPerProductLine;
  }

  public get groupId(): string {
    return this._groupId;
  }

  // Setters

  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  /**
   * The button is disabled if at least one product line has the status `MISSING_RECIPIENTS`.
   */
  public get isSubmitButtonDisabled(): boolean {
    return this.carriersPerProductLine.some(
      (productLineDelegate) => productLineDelegate.productLine.status === ProductLineCardStatus.MISSING_RECIPIENTS,
    );
  }

  // Api calls
  public async getInitialData(cartId: string): Promise<void> {
    this.setIsLoading(true);

    const cart = await getCart(cartId);
    await runInAction(async () => {
      if (!cart) throw new Error(`Cart with such id doesn't exist: ${cartId}`);
      this._groupId = cart.groupId;

      const { state, effectiveDate } = cart.lastUpdateBrowsingCriteria;

      const availableCarriers = await getAvailableCarriers(state, effectiveDate, cart.rateSegment);
      runInAction(() => {
        const sortedCardsByPLineId = availableCarriers.sort((a, b) =>
          sortByProductLineId(a.productLineId, b.productLineId),
        );
        this.convertCarriersPerProductLine(sortedCardsByPLineId);
        this.setIsLoading(false);
      });
    });
  }

  private convertCarriersPerProductLine(carrierAvailability: RFQCarrierAvailability[]): void {
    const productLineDelegates = carrierAvailability.map(({ productLineId, carriers }) => {
      const convertedCarriers = carriers.map((carrier) => ({
        ...carrier,
        ccRecipients: [],
        checked: false,
        status: ProductLineCardStatus.NO_CARRIERS_REQUESTED,
      }));

      return new ProductLineDelegate({
        productLineId,
        // Carriers delegates will be created in the product line delegate
        // to pass additional callback
        carriers: convertedCarriers,
        status: ProductLineCardStatus.NO_CARRIERS_REQUESTED,
      });
    });

    this._carriersPerProductLine = productLineDelegates;
  }

  public async submitStep(periodId: string, cartId: string): Promise<void> {
    this.setIsLoading(true);
    const inputCarriersByPLine: InputRFQCarrierByPLine[] = [];

    const selectedProductLines = this.carriersPerProductLine.filter(
      (productLineDelegate) => productLineDelegate.productLine.status === ProductLineCardStatus.INCLUDED_IN_REQUEST,
    );

    selectedProductLines.forEach((productLineDelegate) => {
      const { productLineId, carriers } = productLineDelegate.productLine;

      carriers
        .filter((carrierDelegate) => carrierDelegate.carrier.status === ProductLineCardStatus.INCLUDED_IN_REQUEST)
        .forEach((carrierDelegate) => {
          const { id, defaultRecipientEmail, ccRecipients, sponsorId } = toJS(carrierDelegate.carrier);
          inputCarriersByPLine.push({
            productLineId,
            sponsorId,
            carrierId: id,
            primaryRecipient: defaultRecipientEmail,
            ccRecipients,
            portfolios: [],
          });
        });
    });

    await pickCarriers(periodId, cartId, inputCarriersByPLine);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new PickCarriersPageStore());

export const usePickCarriersStore = (): PickCarriersPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
